import { parsePaddingString } from '../../common-utils';
import { safeParse } from '../../common-utils/math';
import { copy } from '../../common-utils';
import { NodeLike } from '../context/node';
import { TYPES, TEXT_VERTICAL_ALIGN_TYPES } from '../constants';
import { getLineHeightInPx } from './text';

/**
 * Get padding of the node.
 * The priority is
 *  individual side properties > padding object > padding string/number > default 0
 * @param node
 * @returns
 */
export function getPaddingBox(node) {
  const paddingObj = node.padding;
  let pt = 0,
    pr = 0,
    pb = 0,
    pl = 0;

  if (typeof paddingObj === 'object' && paddingObj !== null) {
    pt = paddingObj.top ?? 0;
    pr = paddingObj.right ?? 0;
    pb = paddingObj.bottom ?? 0;
    pl = paddingObj.left ?? 0;
  } else if (typeof paddingObj === 'string' || typeof paddingObj === 'number') {
    [pt, pr, pb, pl] = parsePaddingString(paddingObj);
  }

  const left = safeParse(node.paddingLeft ?? pl);
  const right = safeParse(node.paddingRight ?? pr);
  const top = safeParse(node.paddingTop ?? pt);
  const bottom = safeParse(node.paddingBottom ?? pb);

  return {
    top,
    bottom,
    left,
    right,
    x: left + right,
    y: top + bottom,
  };
}

function removeHiddenProps(node, readed?: Set<unknown>) {
  if (!(node instanceof Object)) return node;
  readed = readed || new Set();
  if (readed.has(node)) {
    throw `readed: ${node}`;
  }
  readed.add(node);
  const newNode = { ...node };
  Object.keys(newNode).forEach((key) => {
    if (key.startsWith('_')) {
      delete newNode[key];
    } else if (newNode[key] instanceof Object) {
      if (Array.isArray(newNode[key])) {
        newNode[key] = newNode[key].map((item) => removeHiddenProps(item, readed));
      } else {
        newNode[key] = removeHiddenProps(newNode[key], readed);
      }
    }
  });
  return newNode;
}

function removeNodes(originNodes) {
  const nodes = {};
  if (!originNodes) return nodes;
  Object.keys(originNodes).map((id) => {
    nodes[id] = removeHiddenProps(originNodes[id]);
  });
  return nodes;
}

export function templateCopy(input) {
  const template = copy(input);
  delete template.props.input;
  return Object.assign({}, template, {
    props: {
      ...template.props,
      nodes: removeNodes(template.props.nodes),
    },
  });
}

export function sortByIndex(a: NodeLike, b: NodeLike) {
  if (a.index < b.index) {
    return -1;
  }
  if (a.index > b.index) {
    return 1;
  }
  return 0;
}

export function createId() {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * 渲染边界接口
 */
export interface RenderBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * 计算节点的实际渲染边界（考虑文字溢出等情况）
 *
 * 对于文字节点，实际渲染区域可能超出 node.x/y/w/h：
 * 1. lineHeight 导致的 bleed（上下左右各扩展 lineHeight/2）
 * 2. 文字内容溢出（根据 alignItems 决定向上/向下/居中扩展）
 *
 * @param renderContext - 渲染上下文，需要包含 providers.textLayout
 * @param node - 节点对象
 * @returns 实际渲染边界 { x, y, w, h }
 */
export function getNodeRenderBounds(renderContext: any, node: any): RenderBounds {
  // 基础边界
  const bounds: RenderBounds = { x: node.x, y: node.y, w: node.w, h: node.h };

  // 文字节点需要考虑溢出
  if (node.type === TYPES.text && node.text) {
    const fontSize = node.fontSize || 12;
    const lineHeightRatio = node.lineHeight ?? 1.35;
    const lineHeight = getLineHeightInPx(fontSize, lineHeightRatio) * 0.5;

    // 计算文字实际高度
    const textLayoutProvider = renderContext?.providers?.textLayout;
    const textLayout = textLayoutProvider?.getTextLayout?.(node.id);
    const measuredHeight = textLayout?.height || node.h;
    const isOverHeight = measuredHeight > node.h;

    // 应用 lineHeight bleed
    bounds.x -= lineHeight / 2;
    bounds.y -= lineHeight / 2;
    bounds.w += lineHeight;
    bounds.h += lineHeight;

    // 处理文字溢出
    if (isOverHeight) {
      const delta = measuredHeight - node.h;
      const alignItems = node.alignItems || TEXT_VERTICAL_ALIGN_TYPES.top;

      switch (alignItems) {
        case TEXT_VERTICAL_ALIGN_TYPES.center:
          bounds.y -= delta / 2;
          bounds.h += delta;
          break;
        case TEXT_VERTICAL_ALIGN_TYPES.bottom:
          bounds.y -= delta;
          bounds.h += delta;
          break;
        default: // top
          bounds.h += delta;
          break;
      }
    }
  }

  return bounds;
}
